import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/authContext';
import { useResourceUsage } from '../../context/ResourceUsageContext';
import { AppLayoutProviders, SidebarLayout } from '../layout/SidebarLayout';
import { notesService } from '../adapters/notes';
import { RecentNote } from '../types';

const PLAN_LIMITS: Record<string, { notes: number; forms: number; chat: number }> = {
  '1': { notes: 15, forms: 5, chat: 10 },
};

function getLimits(subscriptionPlanId?: string) {
  if (!subscriptionPlanId) return { notes: null, forms: null, chat: null };
  const p = PLAN_LIMITS[subscriptionPlanId];
  return p
    ? { notes: p.notes, forms: p.forms, chat: p.chat }
    : { notes: null, forms: null, chat: null };
}

function formatNoteDate(iso: string) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { planData } = useAuth();
  const { usage } = useResourceUsage();

  const [recentNotes, setRecentNotes] = useState<RecentNote[]>([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRecentLoading(true);
    notesService
      .listRecentNotes()
      .then((data) => { if (!cancelled) setRecentNotes(data); })
      .catch(() => { if (!cancelled) setRecentError('Failed to load recent notes.'); })
      .finally(() => { if (!cancelled) setRecentLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const limits = getLimits(planData?.subscriptionPlanId);

  const stats = [
    { name: 'Total Notes', value: usage.notesCount, icon: DocumentTextIcon },
    { name: 'Forms Generated', value: usage.formsCount, icon: ClipboardDocumentListIcon },
    { name: 'AI Chats Used', value: usage.chatCount, icon: ChatBubbleLeftRightIcon },
    { name: 'Renewal Date', value: usage.renewalDate || '—', icon: CalendarDaysIcon },
  ];

  const usageLimits = [
    { key: 'notes', label: 'SOAP Notes', used: usage.notesCount, limit: limits.notes },
    { key: 'forms', label: 'Forms Generated', used: usage.formsCount, limit: limits.forms },
    { key: 'chat', label: 'AI Messages', used: usage.chatCount, limit: limits.chat },
  ];

  return (
    <AppLayoutProviders>
    <SidebarLayout>
    <div className="px-4 sm:px-6 py-6">
      {/* Stats cards */}
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((item) => (
          <div
            key={item.name}
            className="relative overflow-hidden rounded-lg bg-white px-4 pt-3 pb-6 shadow border border-brand-navy/15 sm:px-6 sm:pt-4"
          >
            <dt>
              <div className="absolute rounded-md bg-brand-navy p-3">
                <item.icon className="h-6 w-6 text-white" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-medium text-gray-500">{item.name}</p>
            </dt>
            <dd className="ml-16 flex items-baseline pb-3 sm:pb-4">
              <p className="text-2xl font-semibold text-gray-900">{item.value}</p>
            </dd>
          </div>
        ))}
      </dl>

      {/* Usage limits + Recent notes */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Usage limits */}
        <div className="bg-white shadow rounded-lg p-6 border border-brand-navy/15">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium text-brand-navy">Usage Limits</h3>
              <p className="text-xs text-gray-500">Based on your current plan</p>
            </div>
            <span className="text-xs text-gray-500">Resets monthly</span>
          </div>
          <div className="space-y-3">
            {usageLimits.map((row) => {
              const percent = row.limit
                ? Math.min(100, Math.round((row.used / row.limit) * 100))
                : 0;
              const barClass = percent > 90 ? 'bg-red-600' : 'bg-brand-navy';
              return (
                <div key={row.key} className="rounded-lg border border-gray-200 px-4 py-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-900">{row.label}</span>
                    <span className="text-xs text-gray-500">
                      {row.limit ? `${row.used}/${row.limit}` : `${row.used}/Unlimited`}
                    </span>
                  </div>
                  <div className="mt-2 h-2 w-full rounded-full bg-gray-100">
                    <div
                      role="progressbar"
                      aria-valuenow={percent}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${row.label} usage`}
                      className={`h-2 rounded-full ${barClass}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                  <div className="mt-1 text-[11px] text-gray-500">
                    {row.limit ? `${percent}% used` : 'Unlimited'}
                  </div>
                </div>
              );
            })}
          </div>
          <button
            onClick={() => navigate('/profile?tab=payments')}
            className="mt-4 w-full rounded-lg bg-brand-navy px-4 py-3.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-navy/90 focus:outline-none focus:ring-2 focus:ring-brand-accent"
          >
            Increase limits
          </button>
        </div>

        {/* Recent notes */}
        <div className="bg-white shadow rounded-lg p-6 border border-brand-navy/15">
          <h3 className="text-lg font-medium text-brand-navy mb-4">Recent Notes</h3>
          {recentLoading && (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500">Loading recent notes…</p>
            </div>
          )}
          {recentError && (
            <div className="text-center py-12">
              <p className="text-sm text-red-600">{recentError}</p>
            </div>
          )}
          {!recentLoading && !recentError && recentNotes.length === 0 && (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No recent notes</p>
              <p className="text-xs text-gray-400 mt-1">Notes being processed will appear here</p>
            </div>
          )}
          {!recentLoading && !recentError && recentNotes.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {recentNotes.map((n) => {
                const noteType = n.note_type.toUpperCase();
                const dateStr = formatNoteDate(n.created_at);
                return (
                  <button
                    key={n.session_id}
                    onClick={() => navigate(`/patients/${n.patient_id}/notes/${n.session_id}`)}
                    className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-brand-navy/30 hover:bg-brand-navy/5 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-navy/40"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900 truncate">
                            {n.patient_name || n.patient_id}
                          </span>
                          <span
                            className={[
                              'text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded shrink-0',
                              noteType === 'INITIAL'
                                ? 'bg-brand-navy/10 text-brand-navy'
                                : 'bg-brand-accent/10 text-brand-accent',
                            ].join(' ')}
                          >
                            {noteType || 'NOTE'}
                          </span>
                        </div>
                        {dateStr && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <CalendarIcon className="h-3.5 w-3.5" />
                            <span>{dateStr}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
    </SidebarLayout>
    </AppLayoutProviders>
  );
}
